import React, { useState, useEffect, useRef } from 'react';
import DatePicker from '../Common/DatePicker';
import TimePicker from '../Common/TimePicker';
import { createLesson, updateLesson, deleteLesson, uploadMaterials, deleteMaterial } from '../../services/adminService';
import TimecodeEditor from './TimecodeEditor';
import { confirmDialog } from '../Common/confirm';
import { FileIcon, fileColor, fmtSize } from '../Common/fileKind';
import toast from 'react-hot-toast';

// Инлайн-редактор урока для правой панели конструктора.
// Раньше это была всплывашка (Modal) внутри LessonsManager — теперь то же самое,
// но отдельным окном справа, без размытия фона.
const emptyForm = () => ({
  title: '', description: '', recordLink: '',
  date: new Date().toISOString().split('T')[0], time: '18:00',
  duration: '', materials: [], isHomeworkOnly: false, timecodes: [],
});

const fromLesson = (l) => ({
  title: l.title || '',
  description: l.description || '',
  recordLink: l.recordLink || '',
  date: l.date || new Date().toISOString().split('T')[0],
  time: l.time || '18:00',
  duration: l.duration ?? '',
  materials: l.materials || [],
  isHomeworkOnly: l.isHomeworkOnly || false,
  timecodes: l.timecodes || [],
});

// Потолок на файл — держим в одном месте с сервером (MAX_FILE_SIZE в .env)
const MAX_UPLOAD = 512 * 1024 * 1024;

const LessonEditorPane = ({ lesson, sectionId, onSaved, onDeleted, icon, flushRef }) => {
  const isNew = !lesson?.id;
  const [formData, setFormData] = useState(lesson?.id ? fromLesson(lesson) : emptyForm());
  const [uploading, setUploading] = useState(false);
  const [upPct, setUpPct] = useState(0);   // процент отправки файлов
  const [saving, setSaving] = useState(false);
  // Публикация: черновик виден только по кнопке «Опубликовать». dirty — есть несохранённые
  // правки формы; облачко/активная кнопка показываются, пока не опубликовано ИЛИ есть правки.
  const [published, setPublished] = useState(!!lesson?.isPublished);
  const [dirty, setDirty] = useState(false);
  const pubRef = useRef(!!lesson?.isPublished); // защита от повторных запросов при быстром вводе

  // Любое изменение опубликованного урока снимает его с публикации: появляется облако
  // (в списке и в шапке), урок прячется у ученика — публикуется заново кнопкой «Опубликовать».
  const maybeUnpublish = () => {
    if (!pubRef.current || !lesson?.id) return;
    pubRef.current = false;
    setPublished(false);
    updateLesson(lesson.id, { isPublished: false }).then(() => onSaved && onSaved({ silent: true })).catch(() => {});
  };
  // Изменение любого поля формы помечает урок «грязным» и снимает с публикации.
  const set = (patch) => { setFormData(prev => ({ ...prev, ...patch })); setDirty(true); maybeUnpublish(); };

  // Пересобираем форму при смене выбранного урока
  useEffect(() => {
    setFormData(lesson?.id ? fromLesson(lesson) : emptyForm());
    setPublished(!!lesson?.isPublished);
    pubRef.current = !!lesson?.isPublished;
    setDirty(false);
  }, [lesson?.id]);
  // Синхронизируем статус публикации, если он изменился извне (напр. «Опубликовать все»)
  useEffect(() => { setPublished(!!lesson?.isPublished); pubRef.current = !!lesson?.isPublished; }, [lesson?.isPublished]);

  const handleFileUpload = async (files) => {
    if (isNew) { toast.error('Сначала сохраните урок'); return; }
    // Проверяем размер до отправки: иначе большой файл сначала уедет на сервер
    // и только там упрётся в лимит — пользователь ждёт зря.
    const tooBig = [...files].find(f => f.size > MAX_UPLOAD);
    if (tooBig) {
      toast.error(`Файл «${tooBig.name}» больше ${Math.round(MAX_UPLOAD / 1024 / 1024)} МБ`);
      return;
    }
    setUploading(true); setUpPct(0);
    const fd = new FormData();
    for (let f of files) fd.append('materials', f);
    try {
      const response = await uploadMaterials(lesson.id, fd, setUpPct);
      toast.success('Файлы загружены');
      setFormData(prev => ({ ...prev, materials: [...(prev.materials || []), ...response.data] }));
      maybeUnpublish();
      onSaved && onSaved({ silent: true });
    } catch (err) {
      const msg = err?.response?.status === 413
        ? 'Файл слишком большой для сервера'
        : err?.response?.data?.message || 'Ошибка загрузки файлов';
      toast.error(msg);
    } finally {
      setUploading(false); setUpPct(0);
    }
  };

  const handleRemoveMaterial = async (index) => {
    if (isNew) return;
    try {
      await deleteMaterial(lesson.id, index);
      toast.success('Файл удалён');
      setFormData(prev => ({ ...prev, materials: prev.materials.filter((_, i) => i !== index) }));
      maybeUnpublish();
      onSaved && onSaved({ silent: true });
    } catch {
      toast.error('Ошибка при удалении файла');
    }
  };

  const buildPayload = () => ({
    ...formData,
    sectionId,
    duration: formData.duration === '' || formData.duration == null ? null : Number(formData.duration),
    timecodes: (formData.timecodes || [])
      .map(c => ({ t: Number(c.t) || 0, title: (c.title || '').trim() }))
      .sort((a, b) => a.t - b.t),
    isPublished: true, // «Опубликовать» = сохранить + сделать видимым ученику
  });

  // «Опубликовать все» в шапке курса дёргает это перед публикацией: несохранённые
  // правки формы уходят на сервер, и кнопка урока корректно гаснет.
  useEffect(() => {
    if (!flushRef) return undefined;
    flushRef.current = async () => {
      if (isNew || !dirty || !formData.title) return;
      await updateLesson(lesson.id, buildPayload());
      setDirty(false); setPublished(true); pubRef.current = true;
    };
    return () => { flushRef.current = null; };
  });

  const handleSubmit = async () => {
    if (!formData.title) { toast.error('Введите название урока'); return; }
    const payload = buildPayload();
    setSaving(true);
    try {
      if (!isNew) {
        await updateLesson(lesson.id, payload);
        toast.success('Урок опубликован');
        setPublished(true); setDirty(false); pubRef.current = true;
        onSaved && onSaved({ id: lesson.id });
      } else {
        const res = await createLesson(payload);
        toast.success('Урок опубликован');
        setPublished(true); setDirty(false); pubRef.current = true;
        onSaved && onSaved({ id: res.data?.id, created: true });
      }
    } catch (err) {
      const msg = err?.response?.data?.message || err?.message || 'Ошибка при сохранении';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (isNew) { onDeleted && onDeleted(); return; }
    if (await confirmDialog({ title: 'Удалить урок?', message: 'Урок и его материалы будут удалены. Действие необратимо.', confirmText: 'Удалить' })) {
      try {
        await deleteLesson(lesson.id);
        toast.success('Урок удалён');
        onDeleted && onDeleted();
      } catch {
        toast.error('Ошибка при удалении');
      }
    }
  };

  const fileInputId = `matInput-${lesson?.id || 'new'}`;

  return (
    <div className="cc-editor">
      {/* Шапка окна редактирования */}
      <div className="cc-editor-head">
        <div className={`cc-editor-ic${(!published || dirty) ? ' draft' : ''}`} title={(!published || dirty) ? (published ? 'Есть неопубликованные изменения' : 'Черновик — не опубликовано') : undefined}>
          {(!published || dirty)
            ? <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/></svg>
            : (icon || <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"/><path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"/></svg>)}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <input className="cc-inline-title" value={formData.title} onChange={e => set({ title: e.target.value })} placeholder="Название урока" />
          <div className="cc-editor-sub">Ссылки, материалы, дата и время</div>
        </div>
        <button className={`cc-pub-btn ${published && !dirty ? 'done' : 'active'}`} onClick={handleSubmit} disabled={saving || (published && !dirty)} title={published && !dirty ? 'Опубликовано' : 'Опубликовать'}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M18 10h-1.26A8 8 0 1 0 9 20h9a5 5 0 0 0 0-10z"/><polyline points="9 13 12 10 15 13"/><line x1="12" y1="10" x2="12" y2="17"/></svg>
        </button>
        <button className="cc-del-btn" title="Удалить урок" onClick={handleDelete}>
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
        </button>
      </div>

      <div className="cc-editor-body">
        {/* Поля «Ссылка на урок» больше нет: ученик заходит по личной ссылке из профиля */}
        <div className="fl">
          <label className="fl-label">
            <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg></span>
            Ссылка на запись
          </label>
          <div className="fl-iwrap">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg>
            <input className="fl-input with-ic" value={formData.recordLink || ''} onChange={e => set({recordLink: e.target.value })} />
          </div>
        </div>

        {formData.recordLink && (
          <div className="fl">
            <label className="fl-label">
              <span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg></span>
              Таймкоды записи <span className="opt">— главы для ученика</span>
            </label>
            <TimecodeEditor
              recordLink={formData.recordLink}
              value={formData.timecodes}
              onChange={(tc) => set({timecodes: tc })}
            />
          </div>
        )}

        <div className="materials">
          <div className="materials-head2">
            <h4><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>Материалы к уроку</h4>
            <button className="add-file-btn2" onClick={() => document.getElementById(fileInputId).click()} disabled={isNew || uploading}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              {uploading ? `Загрузка ${upPct}%` : 'Добавить'}
            </button>
            <input type="file" id={fileInputId} style={{ display: 'none' }} multiple onChange={e => handleFileUpload(e.target.files)} disabled={isNew} />
          </div>
          <div className="mat-list2">
            {formData.materials?.map((material, index) => {
              const nm = material.filename || material.name || material.title || material;
              const sz = fmtSize(material.size) || '';   // КБ/МБ/ГБ — как у ученика
              // ?name= — сервер отдаёт файл на скачивание под настоящим именем;
              // без этого локальные файлы качались бы под служебным material-...
              const href = material.url && material.url !== '#'
                ? (material.url.includes('/uploads/')
                    ? `${material.url}?name=${encodeURIComponent(nm)}`
                    : material.url)
                : null;
              return (
                <div key={index} className="mat-item2">
                  <span className="mat-ic2" style={{ background: fileColor(nm)[0], color: fileColor(nm)[1] }}><FileIcon name={nm} size={18} /></span>
                  <span className="mat-name2">{nm}</span>
                  {sz && <span className="mat-size2">{sz}</span>}
                  {href && (
                    <a className="mat-dl2" href={href} target="_blank" rel="noopener noreferrer"
                      title="Скачать" aria-label="Скачать" onClick={e => e.stopPropagation()}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    </a>
                  )}
                  <button className="mat-del2" aria-label="Удалить" onClick={() => handleRemoveMaterial(index)}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6M14 11v6"/></svg>
                  </button>
                </div>
              );
            })}
            {(!formData.materials || formData.materials.length === 0) && (
              <div className="mat-empty2">{isNew ? 'Сохраните урок, чтобы добавить материалы' : 'Нет загруженных материалов'}</div>
            )}
          </div>
        </div>

        <div className="fl-grid2">
          <div className="fl">
            <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>Дата проведения</label>
            <DatePicker value={formData.date || ''} onChange={(v) => set({ date: v })} />
          </div>
          <div className="fl">
            <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 7 12 12 15 14"/></svg></span>Время</label>
            <TimePicker value={formData.time || ''} onChange={(v) => set({ time: v })} />
          </div>
        </div>

        <div className="fl">
          <label className="fl-label"><span className="ic"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polyline points="12 8 12 12 14 13"/></svg></span>Длительность урока, минут</label>
          <input className="fl-input" type="number" min="1" step="5" value={formData.duration ?? ''} onChange={e => set({duration: e.target.value })} />
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 6 }}>
            Определяет, сколько урок считается «в эфире» (доступен вход). Если пусто — 120 минут.
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonEditorPane;
